import React, { useState, useEffect, useCallback } from 'react';
import API from '../utils/api';
import toast from 'react-hot-toast';
import { Modal, ConfirmDialog, PageLoader, EmptyState, Badge, ActionBtn, SectionHeader, FormField, StatCard, Pagination } from '../components/common/UI';
import { toShamsi, formatCurrency } from '../utils/dateHelpers';
import { useAuth } from '../context/AuthContext';
import { MdAdd, MdEdit, MdDelete, MdReceipt, MdCategory, MdToday, MdRemoveRedEye } from 'react-icons/md';
import { FaMoneyBillWave, FaChartLine, FaCalendarAlt } from 'react-icons/fa';
import ShamsiDatePicker from '../components/common/ShamsiDatePicker';

const CATEGORIES = ['خوراک', 'حمل و نقل', 'تجهیزات', 'اجاره', 'قبوض', 'تبلیغات', 'سایر'];
const PAYMENT_METHODS = ['نقدی', 'کارت', 'چک', 'حواله'];

const initialForm = {
  title: '', category: 'سایر', amount: '', date: '', paymentMethod: 'نقدی',
  paidTo: '', payer: '', description: '', receipt: ''
};

export default function DailyExpenses() {
  const { isManager } = useAuth();
  const [expenses, setExpenses] = useState([]);
  const [totalExpense, setTotalExpense] = useState(0);
  const [stats, setStats] = useState({ todayTotal: 0, todayCount: 0, dailyAverage: 0, byCategory: [] });
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterCategory, setFilterCategory] = useState('');
  const [dateRange, setDateRange] = useState({ start: '', end: '' });
  const [showModal, setShowModal] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [deleteId, setDeleteId] = useState(null);
  const [viewItem, setViewItem] = useState(null);
  const [form, setForm] = useState(initialForm);
  const [saving, setSaving] = useState(false);

  const fetchExpenses = useCallback(async () => {
    try {
      setLoading(true);
      const params = {};
      if (filterCategory) params.category = filterCategory;
      if (dateRange.start) params.startDate = dateRange.start;
      if (dateRange.end) params.endDate = dateRange.end;
      
      const [expenseRes, statsRes] = await Promise.all([
        API.get('/expenses', { params }),
        API.get('/expenses/stats/daily'),
      ]);
      
      setExpenses(expenseRes.data.data);
      setTotalExpense(expenseRes.data.total);
      setStats(statsRes.data);
    } catch (err) {
      toast.error('خطا در بارگذاری');
    } finally {
      setLoading(false);
    }
  }, [filterCategory, dateRange]);

  useEffect(() => {
    fetchExpenses();
  }, [fetchExpenses]);

  const openAdd = () => { setForm({ ...initialForm, date: new Date() }); setEditItem(null); setShowModal(true); };
  
  const openEdit = (e) => {
    setForm({
      title: e.title, category: e.category, amount: e.amount,
      date: e.date || '', paymentMethod: e.paymentMethod,
      paidTo: e.paidTo || '', description: e.description || ''
    });
    setEditItem(e);
    setShowModal(true);
  };

  const handleSave = async () => {
    if (!form.title || !form.category || !form.amount) {
      return toast.error('عنوان، دسته‌بندی و مبلغ ضروری است');
    }
    setSaving(true);
    try {
      if (editItem) {
        await API.put(`/expenses/${editItem._id}`, form);
        toast.success('مصرف بروزرسانی شد');
      } else {
        await API.post('/expenses', form);
        toast.success('مصرف اضافه شد');
      }
      setShowModal(false);
      fetchExpenses();
    } catch (err) {
      toast.error(err.response?.data?.message || 'خطا');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    try {
      await API.delete(`/expenses/${deleteId}`);
      toast.success('مصرف حذف شد');
      setDeleteId(null);
      fetchExpenses();
    } catch (err) {
      toast.error('خطا در حذف');
    }
  };

  const filtered = expenses.filter(e =>
    !search || e.title.includes(search) || e.paidTo?.includes(search)
  );

  // پیج‌بندی (صفحه‌بندی سمت کلاینت)
  const PAGE_SIZE = 10;
  const [page, setPage] = useState(1);
  useEffect(() => { setPage(1); }, [search, filterCategory, dateRange.start, dateRange.end]);
  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const paged = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const categoryColors = {
    'خوراک': 'border-orange-200 bg-orange-100',
    'حمل و نقل': 'border-blue-200 bg-blue-100',
    'تجهیزات': 'border-purple-200 bg-purple-100',
    'حقوق': 'border-green-200 bg-green-100',
    'اجاره': 'border-red-200 bg-red-100',
    'قبوض': 'border-yellow-200 bg-yellow-100',
    'تبلیغات': 'border-pink-200 bg-pink-100',
    'سایر': 'border-gray-500/20 bg-gray-500/10',
  };

  if (loading) return <PageLoader />;

  return (
    <div className="space-y-5 fade-in">
      <SectionHeader
        title="مصارفات روزانه"
        subtitle={`${filtered.length} مصرف | مجموع: ${formatCurrency(totalExpense)}`}
        actions={isManager && <ActionBtn onClick={openAdd} icon={<MdAdd size={16} />} label="مصرف جدید" />}
      />

      {/* کارت‌های آماری */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <StatCard icon={<FaMoneyBillWave className="text-red-600" />} label="کل مصارفات" value={formatCurrency(totalExpense)} color="red" />
        <StatCard icon={<MdToday className="text-blue-600" />} label="مصارف امروز" value={formatCurrency(stats.todayTotal)} sub={`${stats.todayCount} مورد`} color="blue" />
        <StatCard icon={<MdCategory className="text-purple-600" />} label="بیشترین دسته" value={stats.byCategory?.[0]?._id || '---'} color="purple" />
        <StatCard icon={<FaChartLine className="text-green-600" />} label="میانگین روزانه" value={formatCurrency(stats.dailyAverage || 0)} color="green" />
      </div>

      {/* فیلترها */}
      <div className="glass-card p-4">
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="flex-1">
            <input
              type="text"
              className="form-input"
              placeholder="جستجوی عنوان یا پرداخت به..."
              value={search}
              onChange={e => setSearch(e.target.value)}
            />
          </div>
          <select value={filterCategory} onChange={e => setFilterCategory(e.target.value)} className="form-input sm:w-40">
            <option value="">همه دسته‌ بندی‌ها</option>
            {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
          <ShamsiDatePicker value={dateRange.start} onChange={date => setDateRange({ ...dateRange, start: date })} className="form-input sm:w-36" placeholder="از تاریخ" />
          <ShamsiDatePicker value={dateRange.end} onChange={date => setDateRange({ ...dateRange, end: date })} className="form-input sm:w-36" placeholder="تا تاریخ" />
          <button onClick={() => setDateRange({ start: '', end: '' })} className="px-3 py-2 bg-gray-100 rounded-xl text-sm">پاک کردن</button>
        </div>
      </div>

      {/* نمایش داده‌ها */}
      {filtered.length === 0 ? (
  <EmptyState title="مصرفی یافت نشد" subtitle="اولین مصرف خود را ثبت کنید" />
) : (
  <>
    {/* Desktop Table */}
    <div className="glass-card overflow-hidden hidden md:block">
      <div className="overflow-x-auto">
        <table className="premium-table">
          <thead>
            <tr>
              <th>عنوان</th>
              <th>دسته‌بندی</th>
              <th>مبلغ</th>
              <th>تاریخ</th>
              <th>روش پرداخت</th>
              <th>پرداخت کننده</th>
              <th>پرداخت به</th>
              <th>عملیات</th>
            </tr>
          </thead>
          <tbody>
            {paged.map(e => (
              <tr key={e._id}>
                <td className="font-semibold text-gray-800">{e.title}</td>
                <td><span className="badge bg-gray-100 text-gray-600">{e.category}</span></td>
                <td className="text-red-600 font-medium">{formatCurrency(e.amount)}</td>
                <td className="text-gray-600 text-sm">{toShamsi(e.date)}</td>
                <td className="text-gray-500 text-sm">{e.paymentMethod}</td>
                <td className="text-gray-500 text-sm">{e.payer}</td>
                <td className="text-gray-500 text-sm">{e.paidTo || '---'}</td>
                <td>
                  <div className="flex items-center gap-1">
                    <button onClick={() => setViewItem(e)} className="p-1.5 text-blue-600 hover:text-blue-700 rounded-lg transition-colors">
                      <MdRemoveRedEye size={18} />
                    </button>
                    {isManager && (
                      <button onClick={() => openEdit(e)} className="p-1.5 text-yellow-600 hover:text-yellow-700 rounded-lg transition-colors">
                        <MdEdit size={18} />
                      </button>
                    )}
                    {isManager && (
                      <button onClick={() => setDeleteId(e._id)} className="p-1.5 text-red-600 hover:text-red-700 rounded-lg transition-colors">
                        <MdDelete size={18} />
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>

    {/* Mobile Cards */}
    <div className="md:hidden space-y-3">
      {paged.map(e => (
        <div key={e._id} className="glass-card p-4">
          <div className="flex items-start justify-between mb-2">
            <div>
              <div className="font-semibold text-gray-800">{e.title}</div>
              <div className="text-xs text-gray-500 mt-0.5">{e.category}</div>
            </div>
            <div className="text-red-600 font-bold">{formatCurrency(e.amount)}</div>
          </div>
          <div className="text-xs text-gray-500 space-y-1">
            <div>{toShamsi(e.date)}</div>
            <div>{e.paymentMethod}</div>
            {e.payer && <div>پرداخت کننده: {e.payer}</div>}
            {e.paidTo && <div>پرداخت به: {e.paidTo}</div>}
            {e.description && <div className="text-gray-600 mt-1">{e.description}</div>}
          </div>
          {isManager && (
            <div className="flex gap-2 mt-3">
              <button onClick={() => openEdit(e)} className="flex-1 py-1.5 text-xs bg-gray-50 text-gray-800 rounded-lg">ویرایش</button>
              <button onClick={() => setDeleteId(e._id)} className="flex-1 py-1.5 text-xs bg-red-100 text-red-600 rounded-lg">حذف</button>
            </div>
          )}
        </div>
      ))}
    </div>
    <Pagination page={page} totalPages={totalPages} onChange={setPage} />
  </>
)}

      {/* مودال اضافه/ویرایش */}
      <Modal isOpen={showModal} onClose={() => setShowModal(false)} title={editItem ? 'ویرایش مصرف' : 'مصرف جدید'} size="md">
  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
    <div className="sm:col-span-2">
      <FormField label="عنوان مصرف" required>
        <input className="form-input" value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} placeholder="مثال: خرید ناهار" />
      </FormField>
    </div>
    <FormField label="دسته‌بندی" required>
      <select className="form-input" value={form.category} onChange={e => setForm({ ...form, category: e.target.value })}>
        {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
      </select>
    </FormField>
    <FormField label="مبلغ (افغانی)" required>
      <input type="number" className="form-input" value={form.amount} onChange={e => setForm({ ...form, amount: e.target.value })} placeholder="0" />
    </FormField>
    <FormField label="روش پرداخت">
      <select className="form-input" value={form.paymentMethod} onChange={e => setForm({ ...form, paymentMethod: e.target.value })}>
        {PAYMENT_METHODS.map(m => <option key={m} value={m}>{m}</option>)}
      </select>
    </FormField>
    <FormField label="پرداخت کننده">
      <select className="form-input" value={form.payer} onChange={e => setForm({ ...form, payer: e.target.value })}>
        <option value="">انتخاب کنید</option>
        <option value="دفتر">دفتر</option>
        <option value="جعفری">جعفری</option>
        <option value="ادیب">ادیب</option>
        <option value="نجیب">نجیب</option>
      </select>
    </FormField>
    <FormField label="پرداخت به">
      <input className="form-input" value={form.paidTo} onChange={e => setForm({ ...form, paidTo: e.target.value })} placeholder="نام شخص یا فروشگاه" />
    </FormField>
    <FormField label="تاریخ">
    <ShamsiDatePicker
      value={form.date}
      onChange={(date) => setForm({ ...form, date: date })}
      placeholder="تاریخ مصرف"
      className="form-input"
    />
    </FormField>
    <div className="sm:col-span-2">
      <FormField label="توضیحات">
        <textarea className="form-input h-20 resize-none" value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} placeholder="توضیحات..." />
      </FormField>
    </div>
  </div>
  <div className="flex gap-3 justify-end mt-6 pt-4 border-t border-gray-200">
    <button onClick={() => setShowModal(false)} className="px-5 py-2.5 rounded-xl bg-gray-100 text-gray-800">انصراف</button>
    <ActionBtn onClick={handleSave} label={saving ? 'ذخیره...' : editItem ? 'بروزرسانی' : 'ذخیره'} disabled={saving} />
  </div>
</Modal>

      {/* مودال جزئیات */}
      {viewItem && (
        <Modal isOpen={!!viewItem} onClose={() => setViewItem(null)} title="جزئیات مصرف" size="sm">
          <div className="space-y-3">
            <div className="flex justify-between items-center border-b pb-2">
              <h3 className="text-gray-800 font-bold">{viewItem.title}</h3>
              <span className="badge bg-gray-100">{viewItem.category}</span>
            </div>
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div><span className="text-gray-600">مبلغ:</span> <span className="text-red-600 font-bold">{formatCurrency(viewItem.amount)}</span></div>
              <div><span className="text-gray-600">تاریخ:</span> <span className="text-gray-600">{toShamsi(viewItem.date)}</span></div>
              <div><span className="text-gray-600">روش پرداخت:</span> <span className="text-gray-600">{viewItem.paymentMethod}</span></div>
              <div><span className="text-gray-600">پرداخت به:</span> <span className="text-gray-600">{viewItem.paidTo || '---'}</span></div>
            </div>
            {viewItem.description && <div><span className="text-gray-600 text-sm">توضیحات:</span><p className="text-gray-500 text-sm mt-1">{viewItem.description}</p></div>}
          </div>
        </Modal>
      )}

      <ConfirmDialog isOpen={!!deleteId} onClose={() => setDeleteId(null)} onConfirm={handleDelete} title="حذف مصرف" message="آیا مطمئن هستید؟" />
    </div>
  );
}